import React, { useState, useRef, FocusEvent, FC } from 'react';
import cls from 'classnames';
import MaskedInput from 'react-text-mask';
import { TextEntryStandardProps, MaskedInputFixed } from './TextEntryStandard.types';

import ButtonIcon from '../../button/buttonIcon';
import { EyeIcon, EyeSlashIcon } from '@stashinvest/react-assets';
import maskDict from '../primitives/inputMasks';

const TextEntryStandard: FC<TextEntryStandardProps> = (props) => {
	const {
		className,
		disabled,
		error,
		label,
		assistiveText,
		fieldType = 'simple',
		inputType,
		type = 'text',
		autoComplete = 'off',
		value,
		onFocus,
		onBlur,
		onConceal,
		currencyMaskOptions,
		...inputProps
	} = props;

	// determine input component (masked or regular)
	const inputMask = inputType ? maskDict(inputType,currencyMaskOptions) : undefined;

	// determining if input has any value
	const inputRef = useRef<HTMLInputElement | MaskedInput>(null);

	const inputValue =
		value ||
		(inputMask
			? (inputRef as React.RefObject<MaskedInputFixed>).current?.inputElement?.value
			: (inputRef as React.RefObject<HTMLInputElement>).current?.value);

	// determine if input is focused
	const [isFocused, setFocused] = useState(Boolean(inputValue));
	function handleFocus(event: FocusEvent<HTMLInputElement>) {
		setFocused(true);
		onFocus?.(event);
	}
	function handleBlur(event: FocusEvent<HTMLInputElement>) {
		setFocused(false);
		onBlur?.(event);
	}

	// determine if label should be shrunken or expanded
	const expandLabel = !(isFocused || inputValue || props.placeholder || type === 'date');

	// determine if input conceal icon should render
	const isSensitiveField =
		!disabled && (fieldType === 'sensitive' || type === 'password');

	// determine if input should be concealed or not
	const [concealInput, setConcealInput] = useState(isSensitiveField);
	function handleConcealClick() {
		setConcealInput(!concealInput);
		if (typeof onConceal === 'function') onConceal(!concealInput);
	}

	const commonInputProps = {
		type: isSensitiveField ? (concealInput ? 'password' : 'text') : type,
		disabled: disabled,
		value: value,
		onFocus: handleFocus,
		onBlur: handleBlur,
		autoComplete: autoComplete,
		...inputProps,
	};

	return (
		<div className={cls('text-entry-standard', className)}>
			<label
				className={cls({
					disabled: disabled,
					hasError: error,
					empty: expandLabel,
					sensitive: isSensitiveField,
				})}
			>
				<span>{label}</span>
				{inputMask ? (
					<MaskedInput
						ref={inputRef as React.RefObject<MaskedInput>}
						{...commonInputProps}
						showMask={true}
						guide={false}
						mask={inputMask}
					/>
				) : (
					<input
						ref={inputRef as React.RefObject<HTMLInputElement>}
						{...commonInputProps}
					/>
				)}

				{Boolean(isSensitiveField) ? (
					<ButtonIcon
						onClick={handleConcealClick}
						// label={concealInput ? 'reveal password' : 'hide password'}
						Icon={concealInput ? EyeIcon : EyeSlashIcon}
					/>
				) : null}
			</label>

			{error ? (
				<p className="mt-content2XS captionMediumStandard text-error">{error}</p>
			) : assistiveText ? (
				<p className="mt-content2XS captionMediumStandard text-onSurface">
					{assistiveText}
				</p>
			) : null}
		</div>
	);
};

TextEntryStandard.displayName = 'TextEntryStandard';

export default TextEntryStandard;
